import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { scrollToTop } from 'platform/utilities/scroll';
import { getClaimType } from '~/platform/shared/claims-appeals';
import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';
import { claimTabCrumbHref, claimTabTo } from '../utils/claim-tab-helpers';
import NeedHelp from '../components/NeedHelp';
import ClaimsBreadcrumbs from '../components/ClaimsBreadcrumbs';
import FirstPartyRequestPage from '../components/claim-document-request-pages/FirstPartyRequestPage';
import ThirdPartyRequestPage from '../components/claim-document-request-pages/ThirdPartyRequestPage';
import {
  cancelUpload,
  clearNotification,
  getClaim as getClaimAction,
  resetUploads,
  submitFiles,
} from '../actions';
import * as TrackedItem from '../utils/trackedItemContent';
import { setUpPage, setPageFocus } from '../utils/page';
import withRouter from '../utils/withRouter';
import Default5103EvidenceNotice from '../components/claim-document-request-pages/Default5103EvidenceNotice';
import {
  cstMultiClaimProvider,
  cstSurfaceClosedTrackedItems,
} from '../selectors';
import { isFirstPartyRequest } from '../utils/trackedItemProcessing';

class DocumentRequestPage extends React.Component {
  componentDidMount() {
    this.props.resetUploads();
    TrackedItem.setPageTitle(this.props.trackedItem);
    if (!this.props.loading) {
      setUpPage(true, 'h1');
    } else {
      scrollToTop({ behavior: 'instant' });
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(props) {
    const providerSearch = this.props.location?.search || '';
    const statusPath = this.props.useVaTabs
      ? claimTabTo('status', { search: providerSearch })
      : `../status${providerSearch}`;
    if (!props.loading && !props.trackedItem) {
      this.props.navigate(statusPath, {
        replace: true,
      });
    }
    if (props.uploadComplete) {
      this.handleUploadComplete();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.loading && prevProps.loading) {
      setPageFocus('h1');
      TrackedItem.setPageTitle(this.props.trackedItem);
    }
  }

  handleUploadComplete() {
    const providerSearch = this.props.location?.search || '';
    const statusPath = this.props.useVaTabs
      ? claimTabTo('status', { search: providerSearch })
      : `../status${providerSearch}`;
    const provider = this.props.cstMultiClaimProviderEnabled
      ? this.props.claim?.attributes?.provider
      : null;
    this.props.getClaim(this.props.claim.id, null, provider);
    this.props.navigate(statusPath);
  }

  getRequestPage() {
    const {
      claim,
      closedTrackedItemsEnabled,
      message,
      progress,
      trackedItem,
      type1UnknownErrors,
      uploadResults,
      uploading,
    } = this.props;

    const { uploadMetadata } = claim?.attributes || {};

    const pageProps = {
      acceptedFileTypes: uploadMetadata?.acceptedFileTypes,
      claimId: claim?.id,
      closedTrackedItemsEnabled,
      documentTypes: uploadMetadata?.documentTypeOptions,
      item: trackedItem,
      message,
      onCancel: this.props.cancelUpload,
      onSubmit: files =>
        this.props.submitFiles(claim.id, trackedItem, files, uploadMetadata),
      progress,
      type1UnknownErrors,
      uploadResults,
      uploading,
      useVaTabs: this.props.useVaTabs,
    };

    if (isFirstPartyRequest(trackedItem, closedTrackedItemsEnabled)) {
      return <FirstPartyRequestPage {...pageProps} />;
    }

    return <ThirdPartyRequestPage {...pageProps} />;
  }

  render() {
    const {
      claim,
      closedTrackedItemsEnabled,
      loading,
      location,
      params,
      trackedItem,
    } = this.props;
    const claimType = getClaimType(claim).toLowerCase();

    const previousPageIsFilesTab = () => {
      const previousPage = sessionStorage.getItem('previousPage');
      return previousPage === 'files';
    };

    const providerSearch = location?.search || '';
    const filesPath = this.props.useVaTabs
      ? claimTabCrumbHref('files', { search: providerSearch })
      : `../files${providerSearch}`;
    const statusPath = this.props.useVaTabs
      ? claimTabCrumbHref('status', { search: providerSearch })
      : `../status${providerSearch}`;

    const filesBreadcrumb = {
      href: filesPath,
      label: `Files for your ${claimType} claim`,
      isRouterLink: true,
    };

    const statusBreadcrumb = {
      href: statusPath,
      label: `Status of your ${claimType} claim`,
      isRouterLink: true,
    };

    const previousPageBreadcrumb = previousPageIsFilesTab()
      ? filesBreadcrumb
      : statusBreadcrumb;

    const crumbs = [
      previousPageBreadcrumb,
      {
        href: `../${
          isFirstPartyRequest(trackedItem, closedTrackedItemsEnabled)
            ? 'needed-from-you'
            : 'needed-from-others'
        }/${params.trackedItemId}`,
        label: TrackedItem.setDocumentRequestPageTitle(
          TrackedItem.getLabel(trackedItem),
        ),
        isRouterLink: true,
      },
    ];

    let content;

    if (loading) {
      content = (
        <div>
          <va-loading-indicator
            set-focus
            message="Loading your claim information..."
          />
        </div>
      );
    } else {
      content = TrackedItem.isAutomated5103Notice(trackedItem.displayName) ? (
        <Default5103EvidenceNotice
          item={trackedItem}
          useVaTabs={this.props.useVaTabs}
        />
      ) : (
        <>{this.getRequestPage()}</>
      );
    }

    return (
      <div>
        <div name="topScrollElement" />
        <div className="row">
          <div className="usa-width-two-thirds medium-8 columns">
            <ClaimsBreadcrumbs crumbs={crumbs} />
            <div>{content}</div>
            <NeedHelp item={trackedItem} />
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state, ownProps) {
  const claimsState = state.disability.status;
  const { claimDetail, uploads } = claimsState;

  let trackedItem = null;
  if (claimDetail.detail) {
    const { trackedItems } = claimDetail.detail.attributes;
    const { trackedItemId } = ownProps.params;
    [trackedItem] = trackedItems.filter(
      item => item.id === parseInt(trackedItemId, 10),
    );
  }

  return {
    claim: claimDetail.detail,
    loading: claimDetail.loading,
    message: claimsState.notifications.additionalEvidenceMessage,
    progress: uploads.progress,
    trackedItem,
    type1UnknownErrors: claimsState.notifications.type1UnknownErrors,
    uploadComplete: uploads.uploadComplete,
    uploadResults: uploads.uploadResults,
    uploading: uploads.uploading,
    cstMultiClaimProviderEnabled: cstMultiClaimProvider(state),
    closedTrackedItemsEnabled: cstSurfaceClosedTrackedItems(state) || false,
    useVaTabs: toggleValues(state)[FEATURE_FLAG_NAMES.cstClaimDetailsVaTabs],
  };
}

const mapDispatchToProps = {
  cancelUpload,
  clearNotification,
  getClaim: getClaimAction,
  resetUploads,
  submitFiles,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(DocumentRequestPage),
);

DocumentRequestPage.propTypes = {
  cancelUpload: PropTypes.func,
  claim: PropTypes.object,
  clearNotification: PropTypes.func,
  closedTrackedItemsEnabled: PropTypes.bool,
  cstMultiClaimProviderEnabled: PropTypes.bool,
  getClaim: PropTypes.func,
  loading: PropTypes.bool,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  message: PropTypes.object,
  navigate: PropTypes.func,
  params: PropTypes.object,
  progress: PropTypes.number,
  resetUploads: PropTypes.func,
  submitFiles: PropTypes.func,
  trackedItem: PropTypes.object,
  type1UnknownErrors: PropTypes.array,
  uploadComplete: PropTypes.bool,
  uploadResults: PropTypes.object,
  uploading: PropTypes.bool,
  useVaTabs: PropTypes.bool,
};

export { DocumentRequestPage };
